import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';

/**
 * Two-language UI: Thai for narrators, the interface-as-authored for the admin.
 *
 * The key IS the English source string, and a miss returns the key. That makes
 * the "English" catalogue implicit — it is simply what the code says — so a
 * string that was only ever written in Thai needs no entry and no `t()` call at
 * all: it reads the same in both languages. Only strings a narrator can see are
 * translated; admin-gated screens (Event Log, Sessions, the modals) are left as
 * they are, because a narrator can never reach them.
 *
 * `{placeholder}` substitution is supported for the handful of messages that
 * interpolate values. See CONTEXT.md, "Display language".
 */

const TH = {
  // --- shell / navigation -------------------------------------------------
  // "AD Recorder" (the brand) and "Admin login" deliberately stay English.
  '404 - Page Not Found': '404 - ไม่พบหน้านี้',
  'Sorry, the page you are looking for does not exist.': 'ขอโทษที ไม่มีหน้านี้',
  'Go to Home': 'กลับหน้าแรก',
  'Display language': 'ภาษาที่แสดง',
  'Loading...': 'กำลังโหลด...',
  'Page navigation': 'เปลี่ยนหน้า',
  'Previous': 'ก่อนหน้า',
  'Next': 'ถัดไป',
  'Page {current} of {total}': 'หน้า {current} จาก {total}',

  // --- storage warnings ---------------------------------------------------
  'Storage space is getting low ({percent}% used). Some older cached recordings might be automatically deleted.':
    'พื้นที่เก็บข้อมูลใกล้เต็มแล้ว (ใช้ไป {percent}%) ไฟล์อัดเก่าที่เก็บไว้บางส่วนอาจถูกลบอัตโนมัติ',
  'Storage access is unavailable.': 'ใช้พื้นที่เก็บข้อมูลไม่ได้',
  "Your browser may have blocked storage access or you're in private/incognito mode. Offline recordings won't be saved for retry if uploads fail.":
    'เบราว์เซอร์อาจบล็อกไว้ หรือกำลังใช้โหมดไม่ระบุตัวตนอยู่ ถ้าอัพโหลดไม่สำเร็จจะไม่มีไฟล์อัดเก็บไว้ให้ลองใหม่',

  // --- players ------------------------------------------------------------
  'Video Preview': 'วิดีโอ',
  'Your browser does not support the video tag.': 'เบราว์เซอร์นี้เล่นวิดีโอไม่ได้',
  'Invalid Google Drive URL or could not extract File ID.': 'ลิงก์ Google Drive ไม่ถูกต้อง หรืออ่าน File ID ไม่ได้',

  // --- video list ---------------------------------------------------------
  'Video List': 'รายการวิดีโอ',
  'Failed to load videos': 'โหลดรายการวิดีโอไม่สำเร็จ',
  'Loading videos...': 'กำลังโหลดวิดีโอ...',
  'Search by title...': 'ค้นหาจากชื่อ...',
  'Search': 'ค้นหา',
  'Clear': 'ล้าง',
  'Narrator:': 'ผู้อัด:',
  'Filter by Status:': 'กรองตามสถานะ:',
  'Show Hidden Videos': 'แสดงวิดีโอที่ซ่อนไว้',
  'Videos per page:': 'จำนวนต่อหน้า:',
  // Status filter options — the same words the status column already uses.
  'All': 'ทั้งหมด',
  'New': 'ยังไม่ได้ทำ',
  'Recorded': 'รอการตรวจ',
  'Pending Paid': 'รอการจ่ายตัง',
  'Paid': 'จ่ายแล้ว',
  'Actions': 'จัดการ',
  'No videos found.': 'ไม่พบวิดีโอ',

  // --- video detail -------------------------------------------------------
  'Loading video details...': 'กำลังโหลดข้อมูลวิดีโอ...',
  'Failed to load video details': 'โหลดข้อมูลวิดีโอไม่สำเร็จ',
  'Video not found.': 'ไม่พบวิดีโอนี้',
  'Please try again later.': 'ลองใหม่อีกทีนะ',
  'Go back to list': 'กลับไปหน้ารายการ',
  'Video data not available.': 'ไม่มีข้อมูลวิดีโอ',
  '« Back to Video List': '« กลับไปหน้ารายการวิดีโอ',
  'ID:': 'ไอดี:',
  'Code:': 'รหัส:',
  'Length:': 'ความยาว:',
  'Cost (Won):': 'ค่าตอบแทน (ว่าน):',
  'Cost (Waan):': 'ค่าตอบแทน (หวาน):',
  'Videoe Link:': 'ลิงก์วิดีโอ:',
  'Open Link': 'เปิดลิงก์',
  'Created:': 'วันที่นำเข้า:',
  'Last Updated:': 'อัปเดตล่าสุด:',
  'Status:': 'สถานะ:',
  // The status badge renders video.status.toUpperCase().
  'NEW': 'ยังไม่ได้ทำ',
  'RECORDED': 'รอการตรวจ',
  'PENDING_PAID': 'รอการจ่ายตัง',
  'PAID': 'จ่ายแล้ว',
  'download': 'ดาวน์โหลด',
  'Yes': 'ใช่',
  'No': 'ไม่',
  'No audio description recorded yet.': 'ยังไม่มีเสียงบรรยาย',
  'Audio Description Recording': 'อัดเสียงบรรยาย',
  'Recording uploaded successfully!': 'อัพโหลดเสียงสำเร็จแล้ว!',
  'Reenact is running — stop it to record.': 'กำลังเล่นย้อนอยู่ — หยุดก่อนถึงจะอัดได้',
  'Recording in progress — stop it to reenact.': 'กำลังอัดอยู่ — หยุดก่อนถึงจะเล่นย้อนได้',

  // --- recording ----------------------------------------------------------
  'Recording error': 'อัดเสียงผิดพลาด',
  'Unknown error': 'ไม่ทราบสาเหตุ',
  'getUserMedia not supported on your browser!': 'เบราว์เซอร์นี้อัดเสียงไม่ได้',
  'Could not start recording': 'เริ่มอัดไม่สำเร็จ',
  'No audio data was recorded. Please try again.': 'ไม่มีเสียงที่อัดไว้ ลองอัดใหม่อีกที',
  'Failed to load saved recordings. This may indicate storage permission issues.':
    'โหลดไฟล์อัดที่เก็บไว้ไม่สำเร็จ เบราว์เซอร์อาจไม่อนุญาตให้เก็บข้อมูล',

  // --- reenact ------------------------------------------------------------
  'Reenact': 'เล่นย้อน',
  'Narrator': 'ผู้อัด',
  'Mode': 'โหมด',
  'All (stitched)': 'ทั้งหมด (ต่อกัน)',
  'Single session': 'เซสชันเดียว',
  'Session': 'เซสชัน',
  'Source': 'ไฟล์เสียง',
  'Raw': 'ดิบ',
  'Raw (none)': 'ดิบ (ไม่มี)',
  'Processed': 'ผ่านการประมวลผล',
  'Processed (none)': 'ผ่านการประมวลผล (ไม่มี)',
  'Speed': 'ความเร็ว',
  'Playback speed': 'ความเร็วในการเล่น',
  'Loading…': 'กำลังโหลด…',
  'Stop': 'หยุด',
  'Jump to session': 'ข้ามไปเซสชัน',
  'Jump the narration to session #{number}': 'ข้ามเสียงบรรยายไปเซสชัน #{number}',
  'Session transport': 'ควบคุมเซสชัน',
  'Restart': 'เริ่มใหม่',
  'Prev': 'ก่อนหน้า',
  'Narration playback': 'เล่นเสียงบรรยาย',
  'Your browser does not support the audio tag.': 'เบราว์เซอร์นี้เล่นเสียงไม่ได้',
  'Press play above to start': 'กดเล่นด้านบนเพื่อเริ่ม',
  'Narration volume': 'เสียงบรรยาย',
  'Video volume': 'เสียงวิดีโอ',
  'Narration volume {percent} percent': 'เสียงบรรยาย {percent} เปอร์เซ็นต์',
  'Video volume {percent} percent': 'เสียงวิดีโอ {percent} เปอร์เซ็นต์',
  'Audio link expired': 'ลิงก์เสียงหมดอายุ',
  'Refresh link': 'ขอลิงก์ใหม่',
  'No reenactable session for this narrator.': 'ผู้อัดคนนี้ยังไม่มีเซสชันให้เล่นย้อน',
  'No audio for this source — try switching Raw/Processed.': 'ไม่มีไฟล์เสียงแบบนี้ — ลองสลับดิบ/ผ่านการประมวลผลดู',
  'Could not load reenact data': 'โหลดข้อมูลเล่นย้อนไม่สำเร็จ',
  'Could not refresh audio link': 'ขอลิงก์เสียงใหม่ไม่สำเร็จ',
  'Reenact timecodes': 'เวลาเล่นย้อน',
  'Narration time': 'เวลาเสียงบรรยาย',
  'Video time': 'เวลาวิดีโอ',
  'Playing': 'กำลังเล่น',
  'Holding': 'ค้างภาพ',
  'Paused': 'หยุด',
  // (the jumper chips are pure numbers — "#2 · 3/8" over "2:04–2:30" — so they
  // need no entry: a label there was jargon in either language)

  // --- helpers.js ---------------------------------------------------------
  'N/A': '—',
  'Invalid Date': 'วันที่ไม่ถูกต้อง',
  '{minutes}m {seconds}s': '{minutes} นาที {seconds} วิ',
};

const CATALOGUES = { th: TH, en: {} };

export const LANGUAGES = ['th', 'en'];

// The active language also lives here, outside React, so `t()` can be called
// from plain modules (utils/helpers.js) and from imperative code such as the
// window.alert()/confirm() calls in VideoListPage.
let activeLanguage = 'en';

export const getLanguage = () => activeLanguage;

const fill = (text, params) => (
  params
    ? text.replace(/\{(\w+)\}/g, (match, name) => (
        Object.prototype.hasOwnProperty.call(params, name) ? String(params[name]) : match
      ))
    : text
);

/**
 * Translate `key` into the active language. A missing entry falls back to the
 * key itself — which is the admin/English string, by construction.
 */
export const t = (key, params) => fill(CATALOGUES[activeLanguage]?.[key] ?? key, params);

const LanguageContext = createContext({ language: 'en', setLanguage: () => {} });

export const useLanguage = () => useContext(LanguageContext);

/**
 * `t`, for components that render translated text.
 *
 * Always prefer this over importing `t` directly in a component: reading the
 * context is what subscribes the component to a language change. Without it a
 * React.memo'd component (VideoRow, VideoTable) — or any subtree React bails out
 * of, since the provider's `children` element is referentially stable — would
 * keep rendering the previous language after a switch.
 */
export const useT = () => {
  useContext(LanguageContext);
  return t;
};

/**
 * Mount inside AuthProvider — the default language comes from the role. A manual
 * switch is React state only, so it lasts until a reload or a login/logout and
 * "log in ⇒ English" holds unconditionally.
 */
export const LanguageProvider = ({ children }) => {
  const { isAdmin } = useAuth();
  const [override, setOverride] = useState(null);

  const language = override || (isAdmin ? 'en' : 'th');
  // Set during render, not in an effect: `t()` reads this module variable, and
  // the children below render before any effect runs — deferring it would show
  // one frame of the wrong language.
  activeLanguage = language;

  // Logging in or out drops a manual override back to the role default.
  useEffect(() => { setOverride(null); }, [isAdmin]);

  // Browser translation is off either way (see public/index.html); `lang` is set
  // for screen readers and text rendering.
  useEffect(() => { document.documentElement.lang = language; }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage: setOverride }}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
